import { FC, useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "components/ui/Button";
import { EyeIcon } from "components/icons/Icons";
import { TableAction } from "components/display/Table/types";
import { OnCreateConfig } from "shared/contexts/table-context/components/types";
import { plateformesAPI } from "services/swagger-api";
import { CourtierListItem } from "api/courtiers";
import CourtiersView from "back-office/features/courtiers-view/CourtiersView";
import { PersonneMoraleQuickView } from "back-office/shared/components/personnes-morales/PersonneMoraleQuickView";
import { Sizes } from "enums/sizes";
import { useDialog } from "hooks/useDialog";

import { getCorporate } from "./api/corporateApi";
import { estAdministree as estModeAdministree } from "./modeGestion";

interface PlateformeBrokersProps {
  plateformeId: string;
  /** Mode de gestion de la plateforme — pilote le badge de la ligne titulaire. */
  modeGestion?: string;
  onCreateConfig?: OnCreateConfig;
}

// On est déjà dans le périmètre de l'orga : filtrer par plateforme n'a pas de sens ici
const HIDDEN_FACETS = ['plateformes'];

/**
 * Courtiers d'une plateforme : la nouvelle liste admin des courtiers, filtrée sur
 * la plateforme de la page (facettes/tri dispo). N'affiche par défaut que les colonnes
 * utiles à ce contexte ; conserve les actions voir + retirer de la plateforme.
 */
export const PlateformeBrokers: FC<PlateformeBrokersProps> = ({
  plateformeId,
  modeGestion,
  onCreateConfig
}) => {
  const { openBlankDialog, closeDialog } = useDialog();
  const [refresh, setRefresh] = useState(0);

  // PM titulaire de la plateforme : sa ligne porte le badge de gouvernance
  // (« Gère la plateforme », ou « Représente la plateforme » en Administrée).
  const [gerantePmId, setGerantePmId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getCorporate(plateformeId)
      .then((corporate) => {
        if (!cancelled) setGerantePmId(corporate?.personneMoraleId ?? null);
      })
      .catch(() => {
        if (!cancelled) setGerantePmId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [plateformeId]);

  // Consultation rapide : la fiche de la PM (même layout que la Gouvernance)
  // et ses membres, sans quitter la plateforme.
  const openQuickView = useCallback(
    (personneMoraleId: string) =>
      openBlankDialog(
        { size: Sizes['5xl'] },
        <PersonneMoraleQuickView personneMoraleId={personneMoraleId} onClose={closeDialog} />,
      ),
    [openBlankDialog, closeDialog],
  );

  const handleRemove = useCallback(
    async (broker: CourtierListItem) => {
      if (!window.confirm(`Retirer « ${broker.name} » de la plateforme ?`)) return;
      await plateformesAPI.unlinkBroker({ plateformeId, brokerId: broker.id });
      setRefresh((r) => r + 1);
    },
    [plateformeId]
  );

  const rowActions: TableAction<CourtierListItem>[] = [
    {
      label: "view",
      render: (broker: CourtierListItem, index: number) => (
        <Button
          key={index}
          color="slate"
          round
          onClick={() => broker.personneMoraleId && openQuickView(broker.personneMoraleId)}
        >
          <EyeIcon />
        </Button>
      )
    },
    {
      label: "remove",
      render: (broker: CourtierListItem, index: number) => (
        <Button
          key={index}
          color="slate"
          round
          onClick={() => handleRemove(broker)}
        >
          <Trash2 size={16} />
        </Button>
      )
    }
  ];

  return (
    <CourtiersView
      embedded
      admin
      titleLabel="Intermédiaires de la plateforme"
      titleDescription="Les intermédiaires — courtiers, mandataires (MIA), professionnels de l'immobilier… — sont les personnes morales habilitées à distribuer les produits d'assurance de la marketplace auprès de leurs clients."
      forcedFilters={{ plateformeIds: [plateformeId], intermediairesSeulement: true }}
      hiddenFacetKeys={HIDDEN_FACETS}
      // Colonnes essentielles par défaut pour ce contexte (masquables via « Colonnes »)
      defaultHiddenColumns={[
        "siret",
        "orias",
        "contactName",
        "lastLoginAt",
        "createdAt",
        "simulationCount",
        "quoteCount"
      ]}
      storageKey="org-courtiers"
      gouvernante={
        gerantePmId
          ? { personneMoraleId: gerantePmId, estAdministree: estModeAdministree(modeGestion) }
          : undefined
      }
      refreshTrigger={refresh}
      rowActions={rowActions}
      createConfig={onCreateConfig}
    />
  );
};
